import { useEffect, useRef, useState } from "react";
import { hasValue } from "Tools";

export function useDocumentTitle(title: string | undefined) {
  useEffect(() => {
    if (title !== undefined) {
      document.title = title;
    }
  }, [title]);
}

// https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
export function useLazyRef<T>(f: () => T) {
  const ref = useRef<T | null>(null);
  if (!hasValue(ref.current)) {
    ref.current = f();
  }
  return ref.current;
}

export function useElement(parent: HTMLElement, f: () => HTMLElement) {
  const ref = useLazyRef(f);

  useEffect(() => {
    parent.appendChild(ref);
    return () => {
      parent.removeChild(ref);
    };
  }, []);

  return ref;
}

export function useDebounce<T>(value: T, delay: number) {
  const ref = useRef({ handle: null as number | null, value });
  const [debouncedValue, setDebouncedValue] = useState(value);

  ref.current = {
    handle:
      ref.current.handle ??
      window.setTimeout(() => {
        ref.current = { ...ref.current, handle: null };
        setDebouncedValue(ref.current.value);
      }, delay),
    value,
  };

  useEffect(() => {
    return () => {
      ref.current = { ...ref.current, handle: null };
      window.clearTimeout(ref.current.handle!);
    };
  }, [delay]);

  return debouncedValue;
}
